import { useEffect, useState } from "react";
import { api, fmtBRL, fmtInt, fmtBRDateTime } from "@/lib/api";
import { buildParams } from "./pcShared";
import CollaboratorDetail from "./CollaboratorDetail";
import { Medal } from "@phosphor-icons/react";

const MEDALS = ["🥇", "🥈", "🥉"];

export default function CollaboratorsTab({ applied }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("production"); // production | finance

  useEffect(() => {
    setData(null);
    setSelected(null);
    api.get("/production-reports/collaborators", { params: buildParams(applied) }).then(({ data }) => setData(data));
  }, [applied]);

  if (!data) return <div className="tile p-8 text-center text-zinc-400 animate-pulse">Carregando colaboradores...</div>;

  const rows = view === "finance"
    ? [...data.collaborators].sort((a, b) => b.finalized_value - a.finalized_value)
    : data.collaborators;

  return (
    <div className="space-y-3" data-testid="pc-collaborators-tab">
      <div className="flex items-center gap-2">
        <button onClick={() => setView("production")}
          className={`text-xs px-3 py-1 border rounded ${view === "production" ? "bg-black text-white border-black" : "border-zinc-300"}`}
          data-testid="pc-collab-view-production">Produção</button>
        <button onClick={() => setView("finance")}
          className={`text-xs px-3 py-1 border rounded ${view === "finance" ? "bg-black text-white border-black" : "border-zinc-300"}`}
          data-testid="pc-collab-view-finance">Faturamento</button>
        <div className="text-[10px] text-zinc-500 ml-auto">
          Total: {fmtInt(data.totals.pieces)} peças · {fmtBRL(data.totals.finalized_value)} finalizado · média {fmtInt(data.avg_pieces)} pçs/colab
        </div>
      </div>

      <div className="tile overflow-x-auto">
        <table className="table w-full text-xs">
          <thead>
            {view === "production" ? (
              <tr>
                <th>#</th><th>Colaborador</th><th>Setor</th><th className="text-right">Movimentos</th>
                <th className="text-right">Pedidos</th><th className="text-right">Peças</th><th className="text-right">SKUs</th>
                <th className="text-right">Dias ativos</th><th className="text-right">Horas est.</th>
                <th className="text-right">Peças/dia</th><th className="text-right">Retrab.</th><th>Última atividade</th>
              </tr>
            ) : (
              <tr>
                <th>#</th><th>Colaborador</th><th>Setor</th><th className="text-right">Pedidos finalizados</th>
                <th className="text-right">Valor finalizado</th><th className="text-right">Valor médio/pedido</th>
                <th className="text-right">% da fábrica</th><th>Participação</th>
              </tr>
            )}
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.code || r.name}
                className={`cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800 ${selected === (r.code || r.name) ? "bg-yellow-50 dark:bg-zinc-800" : ""}`}
                onClick={() => setSelected(selected === (r.code || r.name) ? null : (r.code || r.name))}
                data-testid={`pc-collab-row-${r.code || r.name}`}>
                <td className="font-mono">{i < 3 ? MEDALS[i] : i + 1}</td>
                <td className="font-bold">{r.name}</td>
                <td className="text-zinc-500">{r.sector || "—"}</td>
                {view === "production" ? (
                  <>
                    <td className="text-right font-mono">{fmtInt(r.movements)}</td>
                    <td className="text-right font-mono">{fmtInt(r.orders)}</td>
                    <td className="text-right font-mono font-bold">{fmtInt(r.pieces)}</td>
                    <td className="text-right font-mono">{r.skus}</td>
                    <td className="text-right font-mono">{r.active_days}</td>
                    <td className="text-right font-mono">{r.est_hours}h</td>
                    <td className="text-right font-mono">{fmtInt(r.pieces_per_day)}</td>
                    <td className={`text-right font-mono ${r.reworks > 0 ? "text-yellow-600 font-bold" : ""}`}>{r.reworks}</td>
                    <td className="font-mono text-zinc-500">{fmtBRDateTime(r.last_activity)}</td>
                  </>
                ) : (
                  <>
                    <td className="text-right font-mono">{fmtInt(r.finalized_orders)}</td>
                    <td className="text-right font-mono font-bold">{fmtBRL(r.finalized_value)}</td>
                    <td className="text-right font-mono">{fmtBRL(r.finalized_orders ? r.finalized_value / r.finalized_orders : 0)}</td>
                    <td className="text-right font-mono">{r.share_value_pct}%</td>
                    <td>
                      <div className="w-24 h-2 bg-zinc-200 dark:bg-zinc-700 rounded overflow-hidden">
                        <div className="h-full bg-[#39D98A]" style={{ width: `${Math.min(100, r.share_value_pct)}%` }} />
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={12} className="text-center text-zinc-400 py-6">Nenhuma movimentação no período</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && <CollaboratorDetail code={selected} applied={applied} />}
      {!selected && rows.length > 0 && (
        <div className="text-[10px] text-zinc-400 flex items-center gap-1">
          <Medal size={12} /> Clique em um colaborador para abrir o perfil completo com todos os pedidos executados.
        </div>
      )}
    </div>
  );
}
